/**
 * SocialCapital Passport — public & self-view.
 *
 * Routes:
 *   /sc/passport/:scid    — public passport for anyone with a SCID
 *   /sc/me                — signed-in user's own passport (mints SCID on first hit)
 *
 * The passport shows: balance, per-category totals, recent 20 ledger rows,
 * and a shortcut to run the hash-chain integrity check.
 */
import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import {
  Shield, CheckCircle2, XCircle, TrendingUp, Award,
  Loader2, RefreshCw, Sparkles, Share2,
} from "lucide-react";
import SocialCapitalHeader from "@/components/socialcapital/SocialCapitalHeader";
import PassportShareCard from "@/components/socialcapital/PassportShareCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/sc`;


function catLabel(slug) {
  if (!slug) return "—";
  return slug.replace(/_/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}


export default function SocialCapitalPassport({ selfMode = false }) {
  const { scid: scidParam } = useParams();
  const [passport, setPassport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    setErr(null);
    try {
      if (selfMode) {
        // Requires an authenticated session cookie / bearer.
        const r = await axios.get(`${API}/me/passport`, { withCredentials: true });
        setPassport(r.data);
      } else if (scidParam) {
        const r = await axios.get(`${API}/passport/${scidParam}`);
        setPassport(r.data);
      }
    } catch (e) {
      setErr(
        e?.response?.status === 401
          ? "Please sign in to see your passport."
          : "Passport not found or unavailable.",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selfMode, scidParam]);

  const runIntegrityCheck = async () => {
    if (!passport?.scid) return;
    setVerifying(true);
    try {
      const r = await axios.get(`${API}/ledger/verify/${passport.scid}`);
      setVerifyResult(r.data);
    } catch {
      setVerifyResult({ ok: false, error: true });
    } finally {
      setVerifying(false);
    }
  };

  return (
    <div
      data-testid="sc-passport"
      className="min-h-screen"
      style={{ background: "var(--wofy-bg)" }}
    >
      <Helmet>
        <title>
          {passport?.scid
            ? `${passport.scid} · Social Capital Passport`
            : "Social Capital Passport"}
        </title>
      </Helmet>

      <SocialCapitalHeader />

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section
        className="border-b"
        style={{ borderColor: "var(--wofy-border)" }}
      >
        <div className="max-w-[1000px] mx-auto px-6 lg:px-10 pt-12 pb-10">
          <div
            className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-5 text-[11px] font-mono uppercase tracking-[0.18em]"
            style={{
              background: "rgba(139,92,246,0.09)",
              color: "#7C3AED",
              border: "1px solid rgba(139,92,246,0.24)",
            }}
          >
            <Shield className="w-3 h-3" />
            SocialCapital passport
          </div>

          {loading ? (
            <div className="flex items-center gap-2 text-sm opacity-70">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : err ? (
            <div
              data-testid="passport-error"
              className="rounded-md border p-6"
              style={{ borderColor: "var(--wofy-border)", background: "white" }}
            >
              <p className="text-[15px]" style={{ color: "var(--wofy-text)" }}>{err}</p>
              {selfMode && (
                <Link
                  to="/login?returnTo=/sc/me"
                  className="wofy-btn-primary inline-flex items-center gap-2 mt-4"
                  style={{ background: "#7C3AED", borderColor: "#7C3AED" }}
                >
                  Sign in
                </Link>
              )}
            </div>
          ) : passport ? (
            <>
              <h1
                data-testid="passport-scid"
                className="font-mono text-3xl md:text-4xl tracking-tight"
                style={{ color: "var(--wofy-text)" }}
              >
                {passport.scid}
              </h1>
              {passport.display_name && (
                <p
                  className="mt-2 text-lg"
                  style={{ color: "var(--wofy-text-soft)" }}
                >
                  {passport.display_name}
                </p>
              )}

              <div className="mt-3">
                <button
                  data-testid="share-passport-btn"
                  onClick={() => setShareOpen(true)}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-[13px] font-semibold text-white"
                  style={{ background: "#7C3AED" }}
                >
                  <Share2 className="w-3.5 h-3.5" /> Share passport
                </button>
              </div>

              <div className="mt-8 grid gap-4 sm:grid-cols-3">
                <StatCard
                  testid="stat-total-points"
                  icon={<Sparkles className="w-4 h-4" />}
                  label="Social Capital"
                  value={passport.balance?.total_points ?? 0}
                />
                <StatCard
                  testid="stat-activities"
                  icon={<Award className="w-4 h-4" />}
                  label="Verified activities"
                  value={passport.balance?.activity_count ?? 0}
                />
                <StatCard
                  testid="stat-adjustments"
                  icon={<TrendingUp className="w-4 h-4" />}
                  label="Ledger adjustments"
                  value={
                    (passport.balance?.adjustment_count ?? 0) +
                    (passport.balance?.reversal_count ?? 0)
                  }
                />
              </div>
            </>
          ) : null}
        </div>
      </section>

      {passport && !err && (
        <>
          {/* ── Per-category breakdown ─────────────────────── */}
          <section className="max-w-[1000px] mx-auto px-6 lg:px-10 py-10">
            <h2
              className="font-display text-xl md:text-2xl mb-5"
              style={{ color: "var(--wofy-text)" }}
            >
              Impact by category
            </h2>
            {passport.by_category?.length ? (
              <div
                data-testid="category-breakdown"
                className="rounded-md border overflow-hidden"
                style={{ borderColor: "var(--wofy-border)", background: "white" }}
              >
                {passport.by_category.map((row, idx) => (
                  <CategoryRow
                    key={row.category}
                    row={row}
                    max={passport.by_category[0].points}
                    isLast={idx === passport.by_category.length - 1}
                  />
                ))}
              </div>
            ) : (
              <div
                data-testid="category-empty"
                className="rounded-md border border-dashed p-8 text-center text-[14px]"
                style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}
              >
                No activity yet. Once a participating organisation verifies your first
                activity, this section will fill up.
              </div>
            )}
          </section>

          {/* ── Ledger / recent rows ─────────────────────────── */}
          <section
            className="border-t"
            style={{ borderColor: "var(--wofy-border)", background: "var(--wofy-surface)" }}
          >
            <div className="max-w-[1000px] mx-auto px-6 lg:px-10 py-10">
              <div className="flex items-baseline justify-between mb-5">
                <h2
                  className="font-display text-xl md:text-2xl"
                  style={{ color: "var(--wofy-text)" }}
                >
                  Recent ledger activity
                </h2>
                <button
                  data-testid="verify-chain-btn"
                  onClick={runIntegrityCheck}
                  disabled={verifying}
                  className="inline-flex items-center gap-2 text-[12px] font-mono uppercase tracking-[0.15em] opacity-80 hover:opacity-100"
                  style={{ color: "#7C3AED" }}
                >
                  {verifying ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="w-3.5 h-3.5" />
                  )}
                  Verify hash chain
                </button>
              </div>

              {verifyResult && (
                <div
                  data-testid="verify-result"
                  className="mb-5 flex items-center gap-2 rounded-md border px-4 py-3 text-[13px]"
                  style={{
                    borderColor: verifyResult.ok ? "rgba(14,124,85,0.30)" : "rgba(220,38,38,0.35)",
                    background: verifyResult.ok ? "rgba(14,124,85,0.06)" : "rgba(220,38,38,0.05)",
                    color: verifyResult.ok ? "#0E7C55" : "#B91C1C",
                  }}
                >
                  {verifyResult.ok ? (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      Chain integrity verified. {verifyResult.rows_checked} rows checked.
                    </>
                  ) : (
                    <>
                      <XCircle className="w-4 h-4" />
                      Chain integrity broken at ledger row{" "}
                      <code className="font-mono">{verifyResult.broken_at || "unknown"}</code>.
                    </>
                  )}
                </div>
              )}

              {passport.recent_activity?.length ? (
                <div
                  data-testid="ledger-rows"
                  className="rounded-md border overflow-hidden"
                  style={{ borderColor: "var(--wofy-border)", background: "white" }}
                >
                  {passport.recent_activity.map((row, idx) => (
                    <LedgerRow
                      key={row.id}
                      row={row}
                      isLast={idx === passport.recent_activity.length - 1}
                    />
                  ))}
                </div>
              ) : (
                <div
                  className="rounded-md border border-dashed p-8 text-center text-[14px]"
                  style={{ borderColor: "var(--wofy-border)", color: "var(--wofy-text-soft)" }}
                >
                  Ledger empty. Every verified activity will appear here as a permanent row.
                </div>
              )}
            </div>
          </section>
        </>
      )}

      {passport && (
        <PassportShareCard
          passport={passport}
          open={shareOpen}
          onClose={() => setShareOpen(false)}
        />
      )}
    </div>
  );
}


function StatCard({ testid, icon, label, value }) {
  return (
    <div
      data-testid={testid}
      className="rounded-md border p-5"
      style={{ borderColor: "var(--wofy-border)", background: "white" }}
    >
      <div className="flex items-center gap-2 mb-2 text-[11px] font-mono uppercase tracking-[0.15em] opacity-70">
        <span style={{ color: "#7C3AED" }}>{icon}</span>
        {label}
      </div>
      <div
        className="font-display text-3xl md:text-4xl leading-none"
        style={{ color: "var(--wofy-text)" }}
      >
        {value.toLocaleString()}
      </div>
    </div>
  );
}


function CategoryRow({ row, max, isLast }) {
  const pct = max > 0 ? Math.min(100, (row.points / max) * 100) : 0;
  return (
    <div
      className="flex items-center gap-4 px-5 py-4"
      style={{ borderBottom: isLast ? "none" : "1px solid var(--wofy-border)" }}
    >
      <div className="flex-1">
        <div
          className="text-[14px] font-medium"
          style={{ color: "var(--wofy-text)" }}
        >
          {catLabel(row.category)}
        </div>
        <div className="mt-1.5 h-1.5 rounded-full overflow-hidden"
             style={{ background: "rgba(139,92,246,0.10)" }}>
          <div
            className="h-full"
            style={{ width: `${pct}%`, background: "#7C3AED" }}
          />
        </div>
      </div>
      <div className="text-right">
        <div
          className="font-mono font-semibold text-[15px]"
          style={{ color: "var(--wofy-text)" }}
        >
          {row.points.toLocaleString()}
        </div>
        <div className="text-[11px] font-mono opacity-60">
          {row.activities} act{row.activities === 1 ? "" : "s"}
        </div>
      </div>
    </div>
  );
}


function LedgerRow({ row, isLast }) {
  const positive = row.points >= 0;
  const dt = row.created_at ? new Date(row.created_at) : null;
  return (
    <div
      className="grid grid-cols-[auto_1fr_auto] items-center gap-4 px-5 py-4"
      style={{ borderBottom: isLast ? "none" : "1px solid var(--wofy-border)" }}
    >
      <div
        className="w-2 h-2 rounded-full"
        style={{ background: positive ? "#0E7C55" : "#B91C1C" }}
      />
      <div>
        <div
          className="text-[14px]"
          style={{ color: "var(--wofy-text)" }}
        >
          {catLabel(row.category)}{" "}
          <span
            className="text-[11px] font-mono uppercase tracking-[0.15em] opacity-60 ml-1"
          >
            · {row.txn_type}
          </span>
        </div>
        <div className="text-[12px] opacity-70">
          {row.reason}
          {row.verification_level != null && (
            <> · verification level {row.verification_level}</>
          )}
          {dt && <> · {dt.toISOString().slice(0, 10)}</>}
        </div>
      </div>
      <div
        className="font-mono font-semibold text-[15px]"
        style={{ color: positive ? "#0E7C55" : "#B91C1C" }}
      >
        {positive ? "+" : ""}{row.points.toLocaleString()}
      </div>
    </div>
  );
}
